import api from './api';

export const getItineraries = async (): Promise<any[]> => {
  const response = await api.get('/itineraries');
  return response.data;
};

export const getItinerary = async (id: number): Promise<any> => {
  const response = await api.get(`/itineraries/${id}`);
  return response.data;
};

export const createItinerary = async (data: any): Promise<any> => {
  const response = await api.post('/itineraries', data);
  return response.data;
};

export const updateItinerary = async (id: number, data: any): Promise<any> => {
  console.log('=== itineraryService.updateItinerary ===');
  console.log('ID:', id);
  console.log('Data keys:', Object.keys(data));
  
  // Log data structure more carefully to avoid truncation
  console.log('Basic info:', data.basic_info);
  console.log('Accommodations count:', data.accommodations?.length);
  if (data.accommodations?.length > 0) {
    console.log('First accommodation:', data.accommodations[0]);
  }
  console.log('Day wise details count:', data.day_wise_details?.length);
  console.log('Additional activities count:', data.additional_activities?.length);
  console.log('Extra expenses count:', data.extra_expenses?.length);
  
  // Validate critical fields before sending
  if (!data.basic_info) {
    throw new Error('Missing basic_info in request data');
  }
  
  if (!data.accommodations || data.accommodations.length === 0) {
    throw new Error('Missing accommodations in request data');
  }
  
  // Check for any undefined or null values that might cause issues
  const cleanedData = JSON.parse(JSON.stringify(data, (key, value) => {
    if (value === undefined) {
      console.warn(`Found undefined value for key: ${key}`);
      return null;
    }
    return value;
  }));
  
  const response = await api.put(`/itineraries/${id}`, cleanedData);
  
  console.log('Response status:', response.status);
  console.log('Response data:', response.data);
  
  return response.data;
};

export const deleteItinerary = async (id: number): Promise<any> => {
  const response = await api.delete(`/itineraries/${id}`);
  return response.data;
};

export const searchItineraries = async (params: any): Promise<any[]> => {
  const response = await api.get('/itineraries/search', { params });
  return response.data;
};

export const getRecentItineraries = async (limit: number = 10, offset: number = 0): Promise<any[]> => {
  const response = await api.get('/itineraries/recent', { params: { limit, offset } });
  return response.data;
};

export const getFeaturedItineraries = async (): Promise<any[]> => {
  const response = await api.get('/itineraries/featured');
  return response.data;
};

export const getCostBreakdown = async (id: number): Promise<any> => {
  const response = await api.get(`/itineraries/${id}/cost-breakdown`);
  return response.data;
};

export const downloadItinerary = async (id: number, isAgent: boolean = false): Promise<void> => {
  try {
    console.log('🔍 itineraryService - Download request for itinerary:', id, 'isAgent:', isAgent);
    
    // For agent downloads, use the dedicated method
    if (isAgent) {
      return downloadAgentItinerary(id);
    }
    
    // For regular downloads, try S3 first
    try {
      const response = await api.get(`/itineraries/${id}/download/docx`);
      if (response.data?.success && response.data?.download_url) {
        console.log('📦 itineraryService - Using S3 download URL');
        
        // Create download link
        const link = document.createElement('a');
        link.href = response.data.download_url;
        link.setAttribute('download', response.data.filename || `WH_${id}.docx`);
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        setTimeout(() => {
          link.remove();
        }, 100);
        return;
      }
    } catch (s3Error) {
      console.log('📦 S3 download failed, trying legacy method:', s3Error);
    }
    
    // Fallback to legacy blob download
    const response = await api.get(`/itineraries/${id}/download`, {
      params: { is_agent: false },
      responseType: 'blob'
    });
    
    console.log('📦 itineraryService - Legacy download response received, size:', response.data?.size);
    
    // Check if response is actually a blob with content
    if (!response.data || response.data.size === 0) {
      console.error('❌ itineraryService - Downloaded file is empty');
      throw new Error('Downloaded file is empty');
    }
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `WH_${id}.docx`);
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      link.remove();
      window.URL.revokeObjectURL(url);
    }, 100);
  } catch (error: any) {
    // If error response is a blob, convert it to text to get the error message
    if (error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      try {
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Download failed');
      } catch {
        throw new Error(text || 'Download failed');
      }
    }
    throw error;
  }
};

export const downloadAgentItinerary = async (id: number): Promise<void> => {
  try {
    console.log('🔍 itineraryService - Agent download request for itinerary:', id);
    
    // Direct call to legacy download for agent itineraries
    const response = await api.get(`/itineraries/${id}/download`, {
      params: { is_agent: true },
      responseType: 'blob'
    });
    
    console.log('📦 itineraryService - Agent download response received, size:', response.data?.size);
    
    // Check if response is actually a blob with content
    if (!response.data || response.data.size === 0) {
      console.error('❌ itineraryService - Downloaded file is empty');
      throw new Error('Downloaded file is empty');
    }
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `WYH_${id}.docx`);
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      link.remove();
      window.URL.revokeObjectURL(url);
    }, 100);
  } catch (error: any) {
    // If error response is a blob, convert it to text to get the error message
    if (error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      try {
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Agent download failed');
      } catch {
        throw new Error(text || 'Agent download failed');
      }
    }
    throw error;
  }
};

export const convertItinerary = async (id: number): Promise<any> => {
  const response = await api.put(`/itineraries/${id}/convert`);
  return response.data;
};

export const releaseItinerary = async (id: number): Promise<any> => {
  const response = await api.post(`/itineraries/${id}/release`);
  return response.data;
};

export const markTentative = async (id: number): Promise<any> => {
  const response = await api.post(`/itineraries/${id}/tentative`);
  return response.data;
};

export const updateBasicInfo = async (id: number, basicInfo: any): Promise<any> => {
  const response = await api.patch(`/itineraries/${id}/basic-info`, basicInfo);
  return response.data;
};

export const updateStatus = async (id: number, status: string): Promise<any> => {
  const response = await api.patch(`/itineraries/${id}/status`, { status });
  return response.data;
};

export const setPrice = async (id: number, priceData: any): Promise<any> => {
  console.log('=== itineraryService.setPrice ===');
  console.log('ID:', id);
  console.log('Price data:', priceData);
  
  const response = await api.put(`/itineraries/${id}/set-price`, priceData);
  
  console.log('Set price response status:', response.status);
  console.log('Set price response data:', response.data);
  
  return response.data;
};

export const itineraryService = {
  getItineraries,
  getItinerary,
  createItinerary,
  updateItinerary,
  updateBasicInfo,
  deleteItinerary,
  searchItineraries,
  getRecentItineraries,
  getFeaturedItineraries,
  getCostBreakdown,
  downloadItinerary,
  downloadAgentItinerary,
  convertItinerary,
  releaseItinerary,
  markTentative,
  updateStatus,
  setPrice
};
